import { io } from "socket.io-client";
import { getLocalStorage, dataLocalStorage } from "src/helper/publicFunction";

let socket = null;
let isConnected = false;

// Get the API URL from environment or use default
const SOCKET_URL =
  import.meta.env.VITE_SOCKET_URL ||
  import.meta.env.VITE_API_URL ||
  "http://localhost:3000";

/**
 * Initialize Socket.IO connection with JWT authentication
 * @param {string} token - JWT token for authentication
 * @returns {Promise<Socket>} Socket instance
 */
export const initializeSocket = async (token) => {
  if (socket && isConnected) {
    console.log("Socket already connected");
    return socket;
  }

  try {
    socket = io(SOCKET_URL, {
      auth: { token: `Bearer ${token}` },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socket.on("connect", () => {
      console.log("Socket connected:", socket.id);
      isConnected = true;
    });

    socket.on("disconnect", (reason) => {
      console.log("Socket disconnected:", reason);
      isConnected = false;
    });

    socket.on("connect_error", (error) => {
      console.error("Socket connection error:", error);
      isConnected = false;
    });

    return socket;
  } catch (error) {
    console.error("Failed to initialize socket:", error);
    throw error;
  }
};

/**
 * Join a manifest chat
 * @param {string} manifestUuid - UUID of the manifest
 * @returns {Promise<Object>} Chat data with manifestUuid, chatUuid, unread counts
 */
export const joinManifestChat = async (manifestUuid) => {
  if (!socket || !isConnected) {
    console.error("Socket not connected");
    throw new Error("Socket not connected");
  }

  return new Promise((resolve, reject) => {
    socket.emit("join_manifest_chat", { manifestUuid }, (ack) => {
      if (ack?.success) {
        console.log("Joined manifest chat:", manifestUuid, ack.data);
        resolve(ack.data);
      } else {
        console.error("Failed to join manifest chat:", ack?.error);
        reject(new Error(ack?.error || "Failed to join chat"));
      }
    });
  });
};

/**
 * Send a message to a manifest chat
 * @param {string} manifestUuid - UUID of the manifest
 * @param {string} text - Message text
 * @returns {Promise<Object>} Message data with unread counts
 */
export const sendMessage = async (manifestUuid, text) => {
  if (!socket || !isConnected) {
    console.error("Socket not connected");
    throw new Error("Socket not connected");
  }

  return new Promise((resolve, reject) => {
    socket.emit("send_message", { manifestUuid, text }, (ack) => {
      if (ack?.success) {
        console.log("Message sent:", ack.data);
        resolve(ack.data);
      } else {
        console.error("Failed to send message:", ack?.error);
        reject(new Error(ack?.error || "Failed to send message"));
      }
    });
  });
};

/**
 * List messages for a manifest chat
 * @param {string} manifestUuid - UUID of the manifest
 * @param {number} limit - Maximum number of messages to retrieve
 * @returns {Promise<Object>} Messages data
 */
export const listMessages = async (manifestUuid, limit = 50) => {
  if (!socket || !isConnected) {
    console.error("Socket not connected");
    throw new Error("Socket not connected");
  }

  return new Promise((resolve, reject) => {
    socket.emit("list_messages", { manifestUuid, limit }, (ack) => {
      if (ack?.success) {
        console.log("Messages listed:", ack.data);
        resolve(ack.data);
      } else {
        console.error("Failed to list messages:", ack?.error);
        reject(new Error(ack?.error || "Failed to list messages"));
      }
    });
  });
};

/**
 * Leave a manifest chat
 * @param {string} manifestUuid - UUID of the manifest
 * @returns {Promise<boolean>} Success status
 */
export const leaveManifestChat = async (manifestUuid) => {
  if (!socket || !isConnected) {
    console.error("Socket not connected");
    throw new Error("Socket not connected");
  }

  return new Promise((resolve, reject) => {
    socket.emit("leave_manifest_chat", { manifestUuid }, (ack) => {
      if (ack?.success) {
        console.log("Left manifest chat:", manifestUuid);
        resolve(ack.data);
      } else {
        console.error("Failed to leave manifest chat:", ack?.error);
        reject(new Error(ack?.error || "Failed to leave chat"));
      }
    });
  });
};

/**
 * Listen for new messages
 * @param {Function} callback - Callback function to handle new messages
 */
export const onNewMessage = (callback) => {
  if (!socket) {
    return;
  }

  socket.on("new_message", callback);
};

/**
 * Listen for messages list
 * @param {Function} callback - Callback function to handle messages list
 */
export const onMessages = (callback) => {
  if (!socket) {
    return;
  }

  socket.on("messages", callback);
};

/**
 * Listen for messages read events
 * @param {Function} callback - Callback function to handle read receipts
 */
export const onMessagesRead = (callback) => {
  if (!socket) {
    return;
  }

  socket.on("messages_read", callback);
};

/**
 * Remove event listener
 * @param {string} event - Event name
 * @param {Function} callback - Callback function to remove
 */
export const off = (event, callback) => {
  if (!socket) {
    return;
  }

  socket.off(event, callback);
};

/**
 * Disconnect socket and cleanup
 */
export const disconnectSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
    isConnected = false;
    console.log("Socket disconnected");
  }
};

/**
 * Check if socket is connected
 * @returns {boolean} Connection status
 */
export const isSocketConnected = () => isConnected;

/**
 * Get socket instance
 * @returns {Socket|null} Socket instance
 */
export const getSocket = () => socket;

/**
 * Auto-join all open manifest chats for admin users
 * This should be called after successful login for admin users
 * @param {Array<string>} manifestUuids - Array of manifest UUIDs to join
 */
export const autoJoinManifestChats = async (manifestUuids = []) => {
  if (!socket || !isConnected) {
    console.error("Socket not connected, cannot auto-join chats");
    return;
  }

  if (!Array.isArray(manifestUuids) || manifestUuids.length === 0) {
    console.log("No manifest chats to auto-join");
    return;
  }

  console.log("Auto-joining manifest chats:", manifestUuids.length);

  for (const manifestUuid of manifestUuids) {
    try {
      await joinManifestChat(manifestUuid);
    } catch (error) {
      console.error(
        `Failed to auto-join manifest chat ${manifestUuid}:`,
        error,
      );
    }
  }

  console.log("Auto-joined all manifest chats");
};
